'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.0.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init off-canvas after mobile menu is ready
		document.addEventListener('mobilemenu-init-done', () => {
			const offCanvas = new OffCanvas({
				debug: false,
				overlay: true
			});
		});

		// Init mobile menu
		const mobileMenu = new MobileMenu();
		mobileMenu.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			accordion: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initAccordion();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 *
	 * Find all links to images and use GLightbox to display them.
	 */
	initImageLinks() {
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);

		if (!parentNode) return;

		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			let figcaption = link.parentNode.querySelector('figcaption');
			let caption = figcaption ? figcaption.textContent : '';

			link.addEventListener('click', e => {
				e.preventDefault();

				const lightbox = GLightbox();
				lightbox.setElements([
					{
						href: link.href,
						type: 'image',
						title: caption
					}
				]);

				lightbox.open();
			});
		});
	}

	/**
	 * Initialize accordion
	 *
	 * By default hide accordion item content and toggle functionality to the button
	 */
	initAccordion() {
		let parentNode = document.querySelector(this.options.accordion.parentSelector);

		if (!parentNode) return;

		parentNode.querySelectorAll('.accordion').forEach(accordionList => {
			let contentNodes = accordionList.querySelectorAll('.accordion__content')
			let toggleNodes = accordionList.querySelectorAll('.accordion__toggle')
			let headerNodes = accordionList.querySelectorAll('.accordion__header')
			let isSingular = accordionList.classList.contains('accordion--singular')

			contentNodes.forEach(node => {
				node.classList.add('accordion__content--hidden')
				node.setAttribute('aria-hidden', true)
			})

			toggleNodes.forEach(node => {
				node.classList.add('accordion__toggle--open')
				let toggleText = node.querySelector('.accordion__toggleText')

				toggleText.innerHTML = toggleText.getAttribute('data-text-open')
			})

			headerNodes.forEach(node => {
				node.addEventListener('click', (e) => {
					let itemEl = e.target.closest('.accordion__item')

					if (!itemEl) return

					let toggleEl = itemEl.querySelector('.accordion__toggle')
					let toggleText = itemEl.querySelector('.accordion__toggleText')
					let contentEl = itemEl.querySelector('.accordion__content')

					if (!contentEl) return

					if (contentEl.classList.contains('accordion__content--hidden')) {
						// Show
						toggleEl.classList.remove('accordion__toggle--open')
						contentEl.classList.remove('accordion__content--hidden')
						contentEl.removeAttribute('aria-hidden')
						toggleText.innerHTML = toggleText.getAttribute('data-text-close')

						// If singular, hide other items
						if (isSingular) {
							contentNodes.forEach(node => {
								if (node !== contentEl) {
									node.classList.add('accordion__content--hidden')
									node.setAttribute('aria-hidden', true)
								}
							})

							toggleNodes.forEach(node => {
								if (node !== toggleEl) {
									node.classList.add('accordion__toggle--open')
									let toggleText = node.querySelector('.accordion__toggleText')
									toggleText.innerHTML = toggleText.getAttribute('data-text-open')
								}
							})
						}
					} else {
						// Hide
						toggleEl.classList.add('accordion__toggle--open')
						contentEl.classList.add('accordion__content--hidden')
						contentEl.setAttribute('aria-hidden', true)
						toggleText.innerHTML = toggleText.getAttribute('data-text-open')
					}

				})
			})
		})

	}
}

window._site = new Site();
